/** The reader's ways past a cut for a paged read - "next page" and "all of it" - moved from the old
 * Evidence screen unchanged. Both of the Library's paged reads (the whole library, and the answer to
 * a question) have the same shape, so the buttons cannot tell them apart. */

import { useEffect, useRef, useState } from "react";

export interface MoreDocuments {
  /** A page is left to read. */
  available: boolean;
  loading: boolean;
  loadingAll: boolean;
  /** The next page failed. The pages already read are still true. */
  error: unknown;
  next: () => void;
  all: () => void;
}

export function useMoreOf(read: {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isFetchNextPageError: boolean;
  error: unknown;
  fetchNextPage: () => Promise<{ isError: boolean; hasNextPage: boolean }>;
}): MoreDocuments {
  const [loadingAll, setLoadingAll] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  async function loadAll() {
    setLoadingAll(true);
    try {
      for (;;) {
        const result = await read.fetchNextPage();
        if (!mounted.current || result.isError || !result.hasNextPage) break;
      }
    } finally {
      if (mounted.current) setLoadingAll(false);
    }
  }
  return {
    available: read.hasNextPage,
    loading: read.isFetchingNextPage,
    loadingAll,
    error: read.isFetchNextPageError ? read.error : null,
    next: () => void read.fetchNextPage(),
    all: () => void loadAll(),
  };
}
